import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  LayoutDashboard, 
  Users, 
  Upload, 
  LogOut, 
  Menu,
  X,
  ChevronRight,
  HelpCircle,
  Calendar,
  BookOpen,
  Building2,
  Mail,
  Target,
  Kanban,
  Beaker,
  Sparkles,
  ArrowRight
} from "lucide-react";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { cn } from "../../lib/utils";

const STRYDE_LOGO = "https://customer-assets.emergentagent.com/job_bd-crm-stryde/artifacts/qdu0hd2a_stryde-fin-01.jpg";
const STRYDE_ICON = "https://customer-assets.emergentagent.com/job_bd-crm-stryde/artifacts/nkizmmkb_stryde-fin-02.jpg";

const navigation = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { name: "Leads", href: "/leads", icon: Users },
  { name: "Companies", href: "/companies", icon: Building2 },
  { name: "Pipeline", href: "/pipeline", icon: Kanban },
  { name: "Feasibilities", href: "/feasibilities", icon: Beaker },
  { name: "Opportunities", href: "/opportunities", icon: Target },
  { name: "Calendar", href: "/calendar", icon: Calendar },
  { name: "Data Management", href: "/import", icon: Upload },
  { name: "Email Search", href: "/email-search", icon: Mail },
  { name: "Email Integration", href: "/email", icon: Mail },
];

// Last-updated banner — concise strip at top of every page
function UpdateBanner() {
  const latest = CHANGELOG[0];
  const bannerKey = `stryde_banner_dismissed_v${latest.version}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(bannerKey) === "true");
  if (dismissed) return null;
  return (
    <div className="bg-gradient-to-r from-amber-50 via-blue-50 to-violet-50 border-b border-slate-200 px-4 lg:px-8 py-2" data-testid="last-updated-banner">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0 text-sm">
          <Sparkles className="h-4 w-4 text-[#D97757] flex-shrink-0" />
          <span className="font-semibold text-slate-700 hidden sm:inline">v{latest.version} ({latest.date}):</span>
          <span className="text-slate-700 truncate">{latest.title}</span>
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem("stryde_changelog_seen");
              window.dispatchEvent(new Event("show-whats-new"));
            }}
            className="text-xs text-[#2E7AD1] hover:underline font-medium whitespace-nowrap"
            data-testid="banner-view-updates-btn"
          >
            View all updates →
          </button>
        </div>
        <button
          type="button"
          onClick={() => { localStorage.setItem(bannerKey, "true"); setDismissed(true); }}
          className="text-slate-400 hover:text-slate-600"
          data-testid="banner-dismiss-btn"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// Changelog entries — newest first. Bump CURRENT_VERSION when adding new items.
const CURRENT_VERSION = 6;
const CHANGELOG = [
  {
    version: 6,
    date: "Feb 2026",
    title: "Email Threads, Trial Segmentation & Powerful Email Search",
    items: [
      "Email Conversations panel on every Lead — view full thread history, click to expand any message, AI Summarize button",
      "Email Correspondence panel on Company pages — matches by domain too (catches unmatched emails)",
      "NEW Email Search page — search across all synced emails with filters (date, tier, owner, direction, domain, company). Replaces imprecise Outlook search",
      "Company Studies segmented by status: Ongoing / Actively Pursuing / Potential / Concluded",
      "Retrospective Evaluation for Concluded trials — 1-5 ⭐ responsiveness rating + notes",
      "Lead↔Study linkage — tag contacts as Potential / Current / Previous trial associates",
      "Lead Role Type — structured CTM/CRA/Coordinator/MSL/PI tagging",
      "Dashboard tiles clickable — drilldown to filtered lists",
    ],
  },
  {
    version: 5,
    date: "Feb 2026",
    title: "Sentiment, AI Summaries & CSM History",
    items: [
      "Lead Sentiment — AI-powered analysis (GPT-4o) or manual override: Positive, Neutral, Cautious, Cold, Negative",
      "AI Note Summaries — generate 2-5 line summaries on any lead note with one click",
      "Company CSM Notes now keep full history with timestamps and meeting context",
      "Signal Type classification (S1-S6) added to Opportunities with filtering",
      "Feasibility creation auto-creates or links to an existing Opportunity",
      "Follow-up cadence editing fixed — manual FU1 changes now cascade to FU2-4",
    ],
  },
  {
    version: 4,
    date: "Feb 2026",
    title: "Pipeline Auto-Advance",
    items: [
      "Pipeline auto-advances from Prospect to Outreach Active when the first email is sent",
      "Works for manual email date entry and Microsoft 365 email sync",
      "Auto-created leads from email sync start at Outreach Active",
      "Stage change is logged in history with 'System (First Email Sent)' attribution",
    ],
  },
  {
    version: 3,
    date: "Apr 2026",
    title: "Visual Modernization",
    items: [
      "Fresh design system — Forest Green & Terracotta palette, Outfit font",
      "Redesigned sidebar, top bar, and all dashboard cards",
      "Consistent color tokens across every page",
    ],
  },
  {
    version: 2,
    date: "Apr 2026",
    title: "Analytics & Intelligence",
    items: [
      "Weekly BD Performance Report with leaderboard & tier activity",
      "Sponsor Intelligence — per-sponsor win/loss table with pending decisions",
      "5-Day Follow-Up Density view (green/amber/red workload)",
      "Daily Digest grouped by cadence profile",
      "Opportunity notification badge in sidebar",
    ],
  },
  {
    version: 1,
    date: "Apr 2026",
    title: "Pipeline, Feasibilities & Filtering",
    items: [
      "Pipeline Kanban Board — drag-and-drop leads across 9 stages",
      "Feasibility Tracking — full CRUD with auto-stage changes on decisions",
      "Therapeutic Area multi-select on Leads with global TA filtering",
      "Win/Loss Reason capture with charts and win-rate %",
      "CSM Alerts for stale Gold/Silver accounts on Dashboard",
      "Indication autocomplete in Opportunity Quick Add",
      "Clickable Lead & Company names across all tables",
      "Lead Connection Manager — link/unlink leads to opportunities with status tracking",
    ],
  },
];

function WhatsNewModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const seen = parseInt(localStorage.getItem("stryde_changelog_seen") || "0", 10);
    if (seen < CURRENT_VERSION) {
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("show-whats-new", handler);
    return () => window.removeEventListener("show-whats-new", handler);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem("stryde_changelog_seen", String(CURRENT_VERSION));
    setOpen(false);
  };

  // Figure out which entries are new for this user
  const seen = parseInt(localStorage.getItem("stryde_changelog_seen") || "0", 10);
  const newEntries = CHANGELOG.filter((e) => e.version > seen);
  const olderEntries = CHANGELOG.filter((e) => e.version <= seen);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent className="max-w-lg max-h-[80vh]" data-testid="whats-new-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-['Outfit']">
            <Sparkles className="h-5 w-5 text-[#D97757]" />
            What's Changed
          </DialogTitle>
          <DialogDescription>
            New features and improvements since your last login
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[55vh] pr-3">
          <div className="space-y-5">
            {newEntries.map((entry) => (
              <div key={entry.version} className="rounded-lg border border-[#D97757]/30 bg-[#FDF6F2] p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm text-[#1C2A25] font-['Outfit']">{entry.title}</h4>
                  <Badge variant="outline" className="text-[10px] border-[#D97757] text-[#D97757]">NEW</Badge>
                </div>
                <p className="text-[11px] text-[#94A3B8] mb-2">{entry.date}</p>
                <ul className="space-y-1.5">
                  {entry.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-[#475569]">
                      <ArrowRight className="h-3 w-3 mt-0.5 text-[#D97757] shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {olderEntries.length > 0 && (
              <>
                <p className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium pt-1">Earlier updates</p>
                {olderEntries.map((entry) => (
                  <div key={entry.version} className="rounded-lg border border-[#E6E4DF] p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm text-[#1C2A25] font-['Outfit']">{entry.title}</h4>
                      <span className="text-[10px] text-[#94A3B8]">{entry.date}</span>
                    </div>
                    <ul className="space-y-1.5">
                      {entry.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-[#64748B]">
                          <ArrowRight className="h-3 w-3 mt-0.5 text-[#94A3B8] shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </>
            )}
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button
            onClick={handleDismiss}
            className="w-full bg-[#2D5A4C] hover:bg-[#23473C]"
            data-testid="whats-new-dismiss-btn"
          >
            Got it, thanks!
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HelpModal() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="gap-2"
          data-testid="help-btn"
        >
          <HelpCircle className="h-5 w-5" />
          <span className="hidden sm:inline">Help</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-['Outfit']">
            <BookOpen className="h-5 w-5 text-[#2D5A4C]" />
            Stryde BD CRM - User Guide
          </DialogTitle>
          <DialogDescription>
            Complete guide for the BD team
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6 text-sm">

            {/* What's New in v6 */}
            <section className="bg-gradient-to-br from-amber-50 via-blue-50 to-violet-50 p-4 rounded-lg border border-amber-200">
              <h3 className="font-semibold text-base text-[#1C2A25] mb-2 font-['Outfit'] flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#D97757]" /> New in v6 (Feb 2026)
              </h3>
              <div className="space-y-3 text-slate-700">
                <div>
                  <strong>📧 Email Conversations on Lead Detail</strong>
                  <p className="text-xs text-slate-600">Every lead now has a full email thread panel. Click any message to view the full body (HTML rendered). Use the per-thread <em>AI Summarize</em> button for instant GPT-4o summaries with key topics, action items, and counterparty sentiment.</p>
                </div>
                <div>
                  <strong>📨 Email Correspondence on Company pages</strong>
                  <p className="text-xs text-slate-600">Company pages show ALL emails matched to that company — including emails sent to <em>anyone</em> at the company's domain (e.g. all @biogen.com emails roll up under Biogen).</p>
                </div>
                <div>
                  <strong>🔎 New "Email Search" page</strong> (sidebar nav)
                  <p className="text-xs text-slate-600">Replaces the imprecise Outlook search. Search subject/sender/recipient/snippet + 8 filters (direction, date range, tier, owner, domain, company, matched-only). Results show real-time query highlights and clickable Company/Lead links.</p>
                </div>
                <div>
                  <strong>⭐ Saved Searches</strong>
                  <p className="text-xs text-slate-600">Save your favorite filter combinations (e.g. "Gold tier responses last 7 days") and re-run with one click. Optionally share with the whole team.</p>
                </div>
                <div>
                  <strong>🧪 Trial Status Segmentation</strong>
                  <p className="text-xs text-slate-600">On every Company page, studies are now grouped into <em>Ongoing</em> / <em>Actively Pursuing</em> / <em>Potential</em> / <em>Concluded</em> sections. Mark a trial as Concluded to unlock the <strong>Retrospective Evaluation</strong> form (1-5 ⭐ responsiveness rating + notes on interaction quality).</p>
                </div>
                <div>
                  <strong>🔗 Lead↔Study Linkage</strong>
                  <p className="text-xs text-slate-600">From any Lead Detail page, link contacts to specific trials at their company with relationship type (Potential / Current / Previous). Lets you track which contacts worked on which projects.</p>
                </div>
                <div>
                  <strong>🏷️ Lead Role Type</strong>
                  <p className="text-xs text-slate-600">New structured role dropdown on every lead: CTM, CRA, Coordinator, MSL, PI, BD/Commercial, Medical Affairs, Other. Sits alongside the existing free-text role field.</p>
                </div>
                <div>
                  <strong>📊 Clickable Dashboard Stats</strong>
                  <p className="text-xs text-slate-600">Gold / Silver / Bronze / Total tier cards + Opportunity tiles (Total / New / CRM Companies / Last 7 Days) are now clickable — drilldown to the filtered list.</p>
                </div>
                <div>
                  <strong>💬 CSM Notes Log-Style</strong>
                  <p className="text-xs text-slate-600">Customer Success notes on Company pages now keep <em>every</em> note as a separate history entry, tagged with meeting date + type. Click any entry to expand/collapse the full text.</p>
                </div>
              </div>
            </section>

            {/* Getting Started */}
            <section>
              <h3 className="font-semibold text-base text-[#1C2A25] mb-2 font-['Outfit']">Getting Started</h3>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li><strong>Dashboard</strong> - Tier stats, pipeline summary, CSM alerts, sponsor intelligence, weekly BD performance, and follow-up density</li>
                <li><strong>Leads</strong> - Browse, filter by tier/TA, search, and edit contacts. Click any name to open their detail page</li>
                <li><strong>Companies</strong> - View leads grouped by company. Click a company name to see its detail page</li>
                <li><strong>Pipeline</strong> - Kanban board to drag-and-drop leads across 9 pipeline stages</li>
                <li><strong>Feasibilities</strong> - Track feasibility submissions, decisions, and win/loss reasons</li>
                <li><strong>Opportunities</strong> - Discover and manage BD opportunities with lead linking</li>
                <li><strong>Calendar</strong> - Visualize emails sent and follow-ups by date</li>
                <li><strong>Email Integration</strong> - Auto-sync emails from Microsoft 365</li>
                <li><strong>Data Management</strong> - Export/import Excel files for bulk updates</li>
              </ul>
            </section>

            {/* BD Team Action Items */}
            <section className="bg-[#E6F2ED] p-4 rounded-lg border border-[#2D5A4C]/20">
              <h3 className="font-semibold text-base text-[#1C2A25] mb-2 font-['Outfit']">What BD Team Needs To Do</h3>
              <ol className="list-decimal list-inside space-y-2 text-[#21533F]">
                <li><strong>CC trials@stryde on all outbound emails</strong> - Leads are auto-created</li>
                <li><strong>Check Dashboard daily</strong> - Review the Daily Digest, past-due follow-ups, and CSM alerts</li>
                <li><strong>Use the Pipeline Board</strong> - Move leads through stages as conversations progress</li>
                <li><strong>Log Feasibilities</strong> - Create a feasibility record when you send one; log the decision when it comes back</li>
                <li><strong>Update Contact Remarks</strong> - Mark contacts as Responded, Interested, Meeting Scheduled, etc.</li>
                <li><strong>Review Weekly Report</strong> - Check your team's weekly BD performance every Monday</li>
              </ol>
            </section>

            {/* Pipeline Board */}
            <section>
              <h3 className="font-semibold text-base text-[#1C2A25] mb-2 font-['Outfit']">Pipeline Board (Kanban)</h3>
              <p className="text-slate-600 mb-2">Drag-and-drop leads across 9 stages:</p>
              <div className="grid grid-cols-3 gap-1 text-xs text-slate-600">
                <span className="bg-slate-100 rounded px-2 py-1">Prospect</span>
                <span className="bg-blue-50 rounded px-2 py-1">Outreach Active</span>
                <span className="bg-amber-50 rounded px-2 py-1">Feasibility Out</span>
                <span className="bg-green-50 rounded px-2 py-1">Site Selected</span>
                <span className="bg-cyan-50 rounded px-2 py-1">In Activation</span>
                <span className="bg-purple-50 rounded px-2 py-1">Enrolling</span>
                <span className="bg-indigo-50 rounded px-2 py-1">Closeout</span>
                <span className="bg-red-50 rounded px-2 py-1">Not Selected</span>
                <span className="bg-teal-50 rounded px-2 py-1">Reactivation</span>
              </div>
              <p className="mt-2 text-slate-500 text-xs">Stage changes are logged automatically. Filter by tier or therapeutic area.</p>
              <div className="mt-2 p-2 bg-[#E6F2ED] rounded text-xs text-[#21533F]">
                <strong>Auto-advance:</strong> Pipeline moves to "Outreach Active" when the first email is sent. Feasibility creation moves to "Feasibility Out". A feasibility win moves to "Site Selected".
              </div>
            </section>

            {/* Feasibilities */}
            <section>
              <h3 className="font-semibold text-base text-[#1C2A25] mb-2 font-['Outfit']">Feasibility Tracking</h3>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li><strong>Create</strong> a feasibility from the Feasibilities page or from a Lead's detail</li>
                <li><strong>Track status</strong>: Sent, Under Review, Approved, Rejected, Withdrawn</li>
                <li><strong>Log decisions</strong> with mandatory Win/Loss reasons</li>
                <li><strong>Auto-stage</strong>: creating a feasibility moves the lead to "Feasibility Out"; a win moves to "Site Selected"</li>
                <li><strong>Stats</strong>: Win-rate %, reasons breakdown, and sponsor-level analysis on the Dashboard</li>
              </ul>
            </section>

            {/* Dashboard Widgets */}
            <section>
              <h3 className="font-semibold text-base text-[#1C2A25] mb-2 font-['Outfit']">Dashboard Widgets</h3>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li><strong>Pipeline Summary</strong> - Leads per stage with velocity metrics</li>
                <li><strong>CSM Alerts</strong> - Gold/Silver accounts with no contact in 60+ days</li>
                <li><strong>Daily Digest</strong> - Today's follow-ups grouped by cadence (BD Relationship, Feasibility, Reactivation)</li>
                <li><strong>5-Day Follow-Up Density</strong> - Workload heatmap (green/amber/red) for the next 5 days</li>
                <li><strong>Sponsor Intelligence</strong> - Per-sponsor win/loss table with pending decisions</li>
                <li><strong>Weekly BD Performance</strong> - Navigate by week; see pipeline moves, wins, losses, top performers</li>
                <li><strong>Opportunity Notifications</strong> - Banner + sidebar badge for new automated opportunities</li>
              </ul>
            </section>

            {/* Opportunities */}
            <section>
              <h3 className="font-semibold text-base text-[#1C2A25] mb-2 font-['Outfit']">Opportunity Management</h3>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li><strong>Quick Add</strong> with indication autocomplete and inline company/lead creation</li>
                <li><strong>Link Leads</strong> to opportunities and track connection status (Contacted, Meeting Set, etc.)</li>
                <li><strong>Clickable names</strong> - Company and lead names link to their detail pages</li>
                <li><strong>Automated discovery</strong> from ClinicalTrials.gov</li>
              </ul>
            </section>

            {/* Therapeutic Areas */}
            <section>
              <h3 className="font-semibold text-base text-[#1C2A25] mb-2 font-['Outfit']">Therapeutic Area Filtering</h3>
              <p className="text-slate-600 mb-2">Leads can be tagged with multiple TAs. Filter by TA on Leads, Companies, and Pipeline pages. Available areas include:</p>
              <div className="flex flex-wrap gap-1 text-xs">
                {["Oncology","Immunology","CNS/Neurology","Rare Disease","Cardiology","Metabolic","Dermatology","Ophthalmology","Infectious Disease","Respiratory","Gastroenterology","Hematology","Women's Health"].map(ta => (
                  <span key={ta} className="bg-slate-100 rounded px-2 py-0.5 text-slate-600">{ta}</span>
                ))}
              </div>
            </section>

            {/* Email Integration */}
            <section>
              <h3 className="font-semibold text-base text-[#1C2A25] mb-2 font-['Outfit']">Email Integration (Auto-Sync)</h3>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li><strong>Syncs every 15 minutes</strong> from connected Microsoft 365 account</li>
                <li><strong>Creates new leads</strong> when you email external business contacts</li>
                <li><strong>Matches replies</strong> to existing leads and updates status</li>
              </ul>
              <div className="mt-3 p-3 bg-[#E6F2ED] rounded-lg border border-[#2D5A4C]/10">
                <p className="text-[#21533F] text-xs">
                  <strong>Best Practice:</strong> Always CC trials@stryde on outreach emails so leads are automatically tracked!
                </p>
              </div>
            </section>

            {/* Tier System */}
            <section>
              <h3 className="font-semibold text-base text-[#1C2A25] mb-2 font-['Outfit']">Tier Prioritization System</h3>
              <div className="space-y-2 text-slate-600">
                <p><span className="inline-block w-3 h-3 bg-yellow-400 rounded-full mr-2"></span><strong>Gold</strong> - Top strategic priorities</p>
                <p><span className="inline-block w-3 h-3 bg-slate-300 rounded-full mr-2"></span><strong>Silver</strong> - High priority targets</p>
                <p><span className="inline-block w-3 h-3 bg-orange-400 rounded-full mr-2"></span><strong>Bronze</strong> - Active monitoring</p>
                <p><span className="inline-block w-3 h-3 bg-slate-200 rounded-full mr-2"></span><strong>Unranked</strong> - New or uncategorized</p>
              </div>
            </section>

            {/* Follow-up Schedule */}
            <section>
              <h3 className="font-semibold text-base text-[#1C2A25] mb-2 font-['Outfit']">Follow-up Cadences</h3>
              <p className="text-slate-600 mb-2">Three built-in cadence profiles:</p>
              <div className="space-y-2 text-xs text-slate-600">
                <div className="bg-slate-50 rounded p-2">
                  <strong>BD Relationship</strong> (14-day cycle) - Standard prospecting follow-ups
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <strong>Feasibility</strong> (7-day cycle) - Faster cadence for active feasibility discussions
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <strong>Reactivation</strong> (30-day cycle) - Gentle re-engagement for dormant contacts
                </div>
              </div>
              <p className="text-slate-600 mt-3 mb-2">Auto-calculated follow-ups from "Date First Email Sent":</p>
              <table className="w-full text-slate-600 border-collapse text-xs">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1">Follow-up</th>
                    <th className="text-left py-1">Working Days</th>
                    <th className="text-left py-1">Approx.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100"><td className="py-1">FU #1</td><td>3 days</td><td>~3-5 days</td></tr>
                  <tr className="border-b border-slate-100"><td className="py-1">FU #2</td><td>7 days</td><td>~1 week</td></tr>
                  <tr className="border-b border-slate-100"><td className="py-1">FU #3</td><td>22 days</td><td>~1 month</td></tr>
                  <tr><td className="py-1">FU #4</td><td>66 days</td><td>~quarterly</td></tr>
                </tbody>
              </table>
            </section>

            {/* Daily Workflow */}
            <section>
              <h3 className="font-semibold text-base text-[#1C2A25] mb-2 font-['Outfit']">Daily Workflow Best Practices</h3>
              <ol className="list-decimal list-inside space-y-1 text-slate-600">
                <li>Check <strong>Dashboard</strong> - review Daily Digest and CSM alerts</li>
                <li>Review <strong>Pipeline Board</strong> - move leads through stages</li>
                <li>Check <strong>5-Day Density</strong> - balance your follow-up workload</li>
                <li>Update <strong>Contact Remarks</strong> after conversations</li>
                <li>Log <strong>Feasibilities</strong> sent and decisions received</li>
                <li>Review <strong>Opportunities</strong> for new automated discoveries</li>
                <li>Plan outreach using <strong>Calendar</strong> view</li>
              </ol>
            </section>

            {/* Contact Remarks */}
            <section>
              <h3 className="font-semibold text-base text-[#1C2A25] mb-2 font-['Outfit']">Contact Remarks (Color Coding)</h3>
              <div className="space-y-1 text-slate-600">
                <p><span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-2"></span><strong>Responded / Interested</strong> - Active engagement</p>
                <p><span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-2"></span><strong>Meeting Scheduled</strong> - Call/meeting planned</p>
                <p><span className="inline-block w-3 h-3 bg-purple-500 rounded-full mr-2"></span><strong>Active Opportunity</strong> - Feasibility or active discussion</p>
                <p><span className="inline-block w-3 h-3 bg-yellow-500 rounded-full mr-2"></span><strong>Pending Follow-up</strong> - Needs follow-up</p>
                <p><span className="inline-block w-3 h-3 bg-red-500 rounded-full mr-2"></span><strong>Not Interested</strong> - Declined or no fit</p>
              </div>
            </section>

            {/* Support */}
            <section className="border-t pt-4">
              <h3 className="font-semibold text-base text-[#1C2A25] mb-2 font-['Outfit']">Need Help?</h3>
              <p className="text-slate-600">
                Contact your BD team lead for additional support or training.
              </p>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [newOppCount, setNewOppCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  
  const user = JSON.parse(localStorage.getItem("stryde_user") || "{}");

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    axios.get(`${API}/dashboard/new-opportunities`).then(res => {
      setNewOppCount(res.data?.count || 0);
    }).catch(() => {});
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("stryde_user");
    navigate("/login");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path.startsWith("/leads/")) return "Lead Details";
    return navigation.find(n => path.startsWith(n.href))?.name || "Dashboard";
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="app-layout">
      <WhatsNewModal />
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-[#151B19] transform transition-transform duration-300 ease-in-out lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full relative overflow-hidden">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
            <Link to="/dashboard" className="flex items-center gap-3">
              <img src={STRYDE_ICON} alt="Stryde" className="h-8 w-8 rounded" />
              <span className="text-white font-medium text-lg font-['Outfit'] tracking-tight">Stryde BD</span>
            </Link>
            <button 
              className="lg:hidden text-[#A1A1AA] hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-0.5">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || 
                (item.href === "/leads" && location.pathname.startsWith("/leads")) ||
                (item.href === "/calendar" && location.pathname === "/calendar");
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200",
                    isActive 
                      ? "bg-[#212B28] text-white border-l-4 border-[#D97757] pl-2.5" 
                      : "text-[#A1A1AA] hover:text-white hover:bg-[#212B28]/60"
                  )}
                  data-testid={`nav-${item.name.toLowerCase().replace(" ", "-")}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="h-5 w-5" strokeWidth={1.5} />
                  {item.name}
                  {item.name === "Opportunities" && newOppCount > 0 && (
                    <span className="ml-auto bg-[#D97757] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                      {newOppCount}
                    </span>
                  )}
                  {isActive && item.name !== "Opportunities" && <ChevronRight className="h-4 w-4 ml-auto opacity-60" />}
                  {isActive && item.name === "Opportunities" && newOppCount === 0 && <ChevronRight className="h-4 w-4 ml-auto opacity-60" />}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-white/10">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-3 w-full p-2 rounded-xl hover:bg-[#212B28] transition-all duration-200"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-9 w-9 bg-[#2D5A4C]">
                    <AvatarFallback className="bg-[#2D5A4C] text-white text-sm font-medium font-['Outfit']">
                      {getInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium text-white font-['Outfit']">{user.name}</div>
                    <div className="text-xs text-[#71717A] truncate">{user.email}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-600"
                  data-testid="logout-btn"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-xl border-b border-[#E6E4DF]">
          <div className="flex items-center justify-between h-full px-4 lg:px-8">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 -ml-2 text-[#52525B] hover:text-[#1C2A25]"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-6 w-6" />
              </button>
              <div className="hidden lg:block">
                <h1 className="text-lg font-semibold text-[#1C2A25] font-['Outfit'] tracking-tight">
                  {getPageTitle()}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                className="gap-2"
                data-testid="whats-new-btn"
                onClick={() => {
                  localStorage.removeItem("stryde_changelog_seen");
                  window.dispatchEvent(new Event("show-whats-new"));
                }}
              >
                <Sparkles className="h-5 w-5 text-[#D97757]" />
                <span className="hidden sm:inline text-sm">What's New</span>
              </Button>
              <HelpModal />
              <img src={STRYDE_LOGO} alt="Stryde Research" className="h-8 hidden sm:block" />
            </div>
          </div>
        </header>

        {/* Last Updated Banner */}
        <UpdateBanner />

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
